import React, { useCallback } from 'react'
import { appInsights } from '../../../../_services/AppInsights'
import { EventType } from '../../../../utility/types'
import { parseCitationText, ParsedTextPart } from '../utils/citationTextParser'

interface CitationPreviewTextProps {
  /** The preview text that may contain file references in [filename] format */
  text: string
  /** Callback when a file reference is clicked */
  onFileClick: (fileName: string, event: React.MouseEvent<HTMLButtonElement>) => void
  /** Optional: The CSS class name to apply to the container paragraph */
  className?: string
  /** Optional: Set to true while loading to disable all file links */
  isLoading?: boolean
  /** Optional: File name that is currently being opened (for visual feedback) */
  openingFileName?: string
}

/**
 * Renders citation preview text with clickable file references
 * Parses text for [filename] patterns and renders them as clickable links
 * Logs telemetry when file references are clicked
 */
export const CitationPreviewText: React.FC<CitationPreviewTextProps> = ({
  text,
  onFileClick,
  className = 'theme-text-subheading text-body-small mb-0 leading-5',
  isLoading = false,
  openingFileName
}) => {
  const parts = parseCitationText(text)

  const handleFileClick = useCallback(
    (fileName: string, event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      event.stopPropagation()

      // Log telemetry for file reference click in preview
      appInsights?.trackEvent({
        name: EventType.CitationPreviewInteraction,
        properties: {
          actionType: 'file_reference_click',
          fileName,
          timestamp: new Date().toISOString()
        }
      })

      onFileClick(fileName, event)
    },
    [onFileClick]
  )

  // If there are no file references, just render the text as is
  if (parts.length === 1 && parts[0].type === 'text') {
    return <p className={className}>{text}</p>
  }

  return (
    <p className={className}>
      {parts.map((part, index) => {
        if (part.type === 'text') {
          return <React.Fragment key={index}>{part.content}</React.Fragment>
        }

        // File reference part
        return (
          <button
            key={index}
            type="button"
            className="text-primary bg-transparent border-0 p-0 m-0 font-inherit cursor-pointer underline disabled:cursor-wait disabled:no-underline disabled:opacity-70 inline"
            onClick={(e) => handleFileClick(part.fileName!, e)}
            disabled={isLoading}
            title={`Click to preview ${part.fileName}`}
            aria-label={`Preview ${part.fileName}`}>
            {openingFileName === part.fileName ? (
              <span className="inline-block">Loading...</span>
            ) : (
              part.content
            )}
          </button>
        )
      })}
    </p>
  )
}
